/**
 * AnnouncementBanner — shows the top admin broadcast (guest + signed-in).
 * Users can dismiss "forever" (stored in localStorage keyed by broadcast id).
 */
import { useMemo, useState } from "react";
import { X, Megaphone } from "lucide-react";
import { useBroadcasts } from "@/hooks/useBroadcasts";

const STORAGE_KEY = "goaldigger.dismissed_broadcasts";

function readDismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
  } catch {
    return [];
  }
}

export function AnnouncementBanner() {
  const { data: broadcasts = [] } = useBroadcasts();
  const [dismissed, setDismissed] = useState<string[]>(() => readDismissed());

  const top = useMemo(
    () => broadcasts.find((b) => !dismissed.includes(b.id)),
    [broadcasts, dismissed],
  );

  if (!top) return null;

  const dismiss = () => {
    const next = [...dismissed, top.id];
    setDismissed(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="rounded-2xl border border-amber-300/60 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-rose-950/30 p-3 sm:p-4 shadow-sm relative">
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        className="absolute top-2 right-2 h-7 w-7 inline-flex items-center justify-center rounded-full text-muted-foreground hover:bg-background/60"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <span className="h-9 w-9 shrink-0 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
          <Megaphone className="h-5 w-5 text-white" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm sm:text-base text-foreground">{top.title}</p>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 whitespace-pre-line">{top.body}</p>
          {top.cta_label && top.cta_url && (
            <a
              href={top.cta_url}
              target={top.cta_url.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="inline-flex items-center mt-2 text-xs font-semibold text-amber-700 dark:text-amber-300 hover:underline"
            >
              {top.cta_label} →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnnouncementBanner;
